/*global google */
import React, { Component, ReactElement } from 'react';
import { render } from 'react-dom';
import { COLOR } from 'const/color';
import { createGroupColors, mapIconColor } from './PlannerMapMapUtils';
import {
  Location,
  middleOfTheWorld,
  OrderListOrderClazz,
  Region,
} from '../../../../types/orderTypes';
import { InstructionType } from '../../../../types/bundleTypes';
import './PlannerMapMap.scss';
import { bigMapMarker, square } from '../../../../components/maps/mapMarkers';
import Button from '../../../../components/buttons/Button';
import { pbMapStyle, zoomMap } from '../../../../components/maps/mapUtils';
import { Dayjs } from 'dayjs';
import { addSelectedOrdersToBundle } from '../../utils/bundleDraft';

export declare interface OrderMarker {
  order: OrderListOrderClazz;
  group: string;
  color: string;
  highlighted: boolean;
  clickable: boolean;
  selected: boolean;
  rightClickable?: boolean;
  bundleId?: string;
}

interface CalculatedOrderMarker extends OrderMarker {
  groupColor: string;
}

class MapMarkerContainer {
  id: string;
  type: InstructionType;
  orderMarkers: CalculatedOrderMarker[];
  mapMarker: google.maps.Marker;

  constructor(
    id: string,
    type: InstructionType,
    orderMarkers: CalculatedOrderMarker[]
  ) {
    this.id = id;
    this.type = type;
    this.orderMarkers = orderMarkers;
  }

  getOrder(): OrderListOrderClazz {
    return this.orderMarkers[0].order;
  }

  getSelectableOrders(): OrderListOrderClazz[] {
    return this.orderMarkers
      .filter(orderMarker => orderMarker.clickable)
      .map(orderMarker => {
        return orderMarker.order;
      });
  }

  getGroup(): string {
    return this.orderMarkers[0].group;
  }

  getLabel(): string {
    if (this.type === InstructionType.Pickup) {
      return 'P';
    } else if (this.isMultiple()) {
      return 'M';
    } else {
      return null;
    }
  }

  getBundleId(): string {
    const marker = this.orderMarkers[0];
    return marker.bundleId ? marker.bundleId : '';
  }

  hasSelected(): boolean {
    return this.orderMarkers.filter(marker => marker.selected).length > 0;
  }

  getColor(): string {
    const uniqueColors = new Set(this.orderMarkers.map(marker => marker.color));
    if (this.isMultiple()) {
      if (this.hasSelected()) {
        // All or some selected?
        if (this.isAllSelected()) {
          return 'rgb(255,0,0)';
        } else {
          return 'rgb(255, 130, 130)';
        }
      } else if (this.hasClickableMarkers()) {
        // Nothing selected but should be selected so white
        return 'rgb(255,255,255)';
      } else if (uniqueColors.size === 1) {
        return uniqueColors.values().next().value;
      } else {
        // No clickable and not selected so it's done
        return 'rgb(100,200,100)';
      }
    } else {
      return this.orderMarkers[0].color;
    }
  }

  getOpacity(): number {
    if (this.orderMarkers.length === 1) {
      return this.orderMarkers[0].highlighted ? 1 : 0.3;
    } else {
      // When the marker is a multiple marker
      return 1;
    }
  }

  getClickable(): boolean {
    if (this.orderMarkers.length === 1) {
      return this.orderMarkers[0].clickable;
    } else {
      return false;
    }
  }

  isRightClickAble(): boolean {
    return this.orderMarkers[0].rightClickable;
  }

  isMultiple(): boolean {
    return this.orderMarkers.length > 1;
  }

  getGroupColor() {
    return this.orderMarkers[0].groupColor;
  }

  private hasClickableMarkers() {
    return this.orderMarkers.filter(marker => marker.clickable).length > 0;
  }

  private isAllSelected() {
    return this.orderMarkers.filter(marker => !marker.selected).length === 0;
  }

  isHighlighted(): boolean {
    return this.orderMarkers.filter(marker => marker.highlighted).length > 0;
  }

  hasVisualChanges(other: MapMarkerContainer) {
    return !(
      this.isHighlighted() === other.isHighlighted() &&
      this.hasSelected() === other.hasSelected() &&
      this.getOpacity() === other.getOpacity() &&
      this.getColor() === other.getColor() &&
      this.getGroupColor() === other.getGroupColor()
    );
  }
}

interface State {
  locationTypes: Set<InstructionType>;
  hideFiltered: boolean;
  expandedMarker: MapMarkerContainer;
  featureId: string;
}

declare interface Props {
  region: Region;
  orders: OrderMarker[];
  updateSelectedOrders: (orders: OrderListOrderClazz[]) => void;
  hubPositions: Location[];
  deliveryAreaBoundary?: {
    features: google.maps.Data.FeatureOptions[];
    type: string;
  };
  onFetchAll: (date: Dayjs) => void;
  date: Dayjs;
  selectedOrders: OrderListOrderClazz[];
}

export default class PlannerMapMap extends Component<Props, State> {
  private mapContainer: HTMLDivElement;

  private mapInstance: google.maps.Map;
  private infoWindow: google.maps.InfoWindow;

  private selectionBox: google.maps.Rectangle = null;
  private selectionBoxStart: google.maps.LatLng = null;
  private shiftHeld = false;

  private markers: MapMarkerContainer[];
  private hubMarkers: google.maps.Marker[];

  public deliveryAreaBorder: google.maps.Polygon;

  constructor(props) {
    super(props);

    this.state = this.blankState();
    this.markers = [];
    this.hubMarkers = [];
  }

  blankState = () => {
    return {
      locationTypes: new Set([
        InstructionType.Pickup,
        InstructionType.Delivery,
      ]),
      hideFiltered: false,
      expandedMarker: null,
      featureId: '',
    };
  };

  componentDidMount() {
    // Set up maps view elements
    console.log(`DrawingMap`);
    this.mapInstance = new google.maps.Map(this.mapContainer, {
      zoom: 10,
      center: middleOfTheWorld,
      clickableIcons: false,
      disableDefaultUI: true,
      mapTypeControl: false,
      zoomControl: true,
      disableDoubleClickZoom: false,
      styles: pbMapStyle,
    });
    const filterDialog = document.getElementById('instruction-type-filter');
    const mapLegend = document.getElementById('planner-map-legend');

    this.infoWindow = new google.maps.InfoWindow({
      disableAutoPan: true,
      maxWidth: 300,
    });

    this.mapInstance.controls[google.maps.ControlPosition.TOP_LEFT].push(
      filterDialog
    );
    this.mapInstance.controls[google.maps.ControlPosition.TOP_RIGHT].push(
      mapLegend
    );

    // Event listeners for the map
    google.maps.event.addListenerOnce(this.mapInstance, 'tilesloaded', () => {
      // wait until map is ready before showing type filter to avoid flashing effect
      setTimeout(() => {
        filterDialog.style.display = 'grid';
        mapLegend.style.display = 'grid';
      }, 500);
    });
    google.maps.event.addListener(this.mapInstance, 'click', clickEvent => {
      this.infoWindow.close();
      this.setState({ expandedMarker: null });
    });
    google.maps.event.addListener(this.infoWindow, 'closeclick', () => {
      this.setState({ expandedMarker: null });
    });

    google.maps.event.addListener(
      this.mapInstance,
      'mousedown',
      mouseDownEvent => {
        if (this.shiftHeld) {
          this.mapInstance.setOptions({
            draggable: false,
          });
          this.selectionBoxStart = mouseDownEvent.latLng;
          this.selectionBox = new google.maps.Rectangle({
            map: this.mapInstance,
            bounds: new google.maps.LatLngBounds(this.selectionBoxStart),
            fillOpacity: 0.15,
            fillColor: '#00FF00',
            strokeWeight: 0.7,
            clickable: false,
          });
        }
      }
    );
    google.maps.event.addListener(
      this.mapInstance,
      'mousemove',
      mouseMoveEvent => {
        if (this.selectionBox) {
          this.selectionBox.setBounds(
            new google.maps.LatLngBounds(this.selectionBoxStart).extend(
              mouseMoveEvent.latLng
            )
          );
        }
      }
    );

    google.maps.event.addListener(this.mapInstance, 'mouseup', mouseUpEvent => {
      if (this.selectionBox) {
        const bounds = new google.maps.LatLngBounds(
          this.selectionBoxStart
        ).extend(mouseUpEvent.latLng);
        const changedMarkers = this.markers
          .filter(markerContainer => {
            return markerContainer.type === InstructionType.Delivery;
          })
          .filter(markerContainer => {
            return bounds.contains(markerContainer.mapMarker.getPosition());
          });

        this.props.updateSelectedOrders(
          changedMarkers.map(marker => marker.getSelectableOrders()).flat(1)
        );

        this.mapInstance.setOptions({
          draggable: true,
        });
        this.selectionBox.setMap(null);
        this.selectionBox = null;
      }
    });

    document.addEventListener('keydown', event => {
      if (event.code === 'ShiftLeft' && event.shiftKey) {
        this.shiftHeld = true;
      }
    });
    document.addEventListener('keyup', event => {
      if (event.code === 'ShiftLeft' && !event.shiftKey) {
        this.shiftHeld = false;
      }
    });

    this.mapInstance.data.setStyle({
      strokeColor: '#661aff',
      strokeOpacity: 0.8,
      strokeWeight: 1,
      fillOpacity: 0.1,
      clickable: false,
    });
    this.hubMarkers = this.generateHubMarkers();
  }

  async componentDidUpdate(prevProps, prevState, snapshot) {
    // Reset filter when changing dates. Can we use more than the length to check? The order ids?
    if (this.props.orders.length !== prevProps.orders.length) {
      this.clearMarkers();
    }
    this.updateMarkers();
    if (this.state.expandedMarker) {
      const targetElement = document.createElement('div');
      render(
        this.infoWindowContentMultiple(
          this.state.expandedMarker,
          this.props.updateSelectedOrders
        ),
        targetElement
      );
      this.infoWindow.setContent(targetElement);
    }
    this.resetAndCreateHubMarkers();

    // Only zoom if new update
    if (this.props.orders.length !== prevProps.orders.length) {
      this.zoomToOrders();
    }
    this.addBoundaryCoordinates();
  }

  private resetAndCreateHubMarkers() {
    this.hubMarkers.forEach(marker => marker.setMap(null));
    this.hubMarkers = [];
    this.generateHubMarkers().forEach(marker => this.hubMarkers.push(marker));
  }

  private addBoundaryCoordinates() {
    if (this.props.region !== this.state.featureId) {
      const feature = this.mapInstance.data.getFeatureById(
        this.state.featureId
      );

      if (feature) {
        this.mapInstance.data.remove(feature);
      }
    }

    if (this.props.deliveryAreaBoundary) {
      const featureId = this.props.deliveryAreaBoundary.features[0].id;

      this.mapInstance.data.addGeoJson(this.props.deliveryAreaBoundary);

      if (this.state.featureId !== featureId) {
        this.setState({ featureId: String(featureId) });
      }
    }
  }

  private zoomToOrders() {
    const allMarkers = this.hubMarkers.concat(
      this.markers.map(marker => marker.mapMarker)
    );
    zoomMap(allMarkers, this.mapInstance);
  }

  clearMarkers = () => {
    this.markers.forEach(container => container.mapMarker.setMap(null));
    this.markers = [];
  };

  addSelectedOrdersToBundleWrapper = async (
    bundleId: string
  ): Promise<void> => {
    const { selectedOrders, onFetchAll, date } = this.props;
    await addSelectedOrdersToBundle(bundleId, selectedOrders);
    await onFetchAll(date);
  };

  updateMarker = (
    existingMarkerContainer: MapMarkerContainer,
    newMarkerContainer: MapMarkerContainer
  ) => {
    if (newMarkerContainer.hasVisualChanges(existingMarkerContainer)) {
      const markerOpts = this.createMarkerOptsForContainer(newMarkerContainer);
      existingMarkerContainer.mapMarker.setOptions(markerOpts);
    }
    if (
      newMarkerContainer.getClickable() !==
      existingMarkerContainer.getClickable()
    ) {
      if (!newMarkerContainer.getClickable()) {
        google.maps.event.clearListeners(
          existingMarkerContainer.mapMarker,
          'click'
        );
      } else {
        existingMarkerContainer.mapMarker.addListener('click', (event: any) => {
          const order = newMarkerContainer.getOrder();
          const bundleId = newMarkerContainer.getBundleId();
          if (event.domEvent.altKey) {
            const anchor = document.createElement('a');
            anchor.href = `/orders/${order.id}`;
            anchor.target = '_blank';
            anchor.rel = 'noopener noreferrer';
            anchor.click();
            anchor.remove();
          } else if (!bundleId) {
            this.props.updateSelectedOrders([order]);
          }
        });
      }
    } else if (
      !newMarkerContainer.getClickable() &&
      newMarkerContainer.getBundleId()
    ) {
      const bundleId = newMarkerContainer.getBundleId();
      google.maps.event.clearListeners(
        existingMarkerContainer.mapMarker,
        'click'
      );
      existingMarkerContainer.mapMarker.addListener('click', (event: any) => {
        if (event.domEvent.altKey && event.domEvent.shiftKey && bundleId) {
          this.addSelectedOrdersToBundleWrapper(bundleId);
          const bundleDraftElement = document.getElementById(bundleId);
          if (bundleDraftElement) {
            bundleDraftElement.scrollIntoView({ behavior: 'smooth' });
            bundleDraftElement.classList.add('map-selection-animation');
          }
        }
      });
    }
    existingMarkerContainer.orderMarkers = newMarkerContainer.orderMarkers;
  };

  markerKey = (orderMarker: OrderMarker, type: InstructionType): string => {
    if (type === InstructionType.Delivery) {
      return `${type}-${orderMarker.order.destinationAddress.location.latitude}-${orderMarker.order.destinationAddress.location.longitude}`;
    } else {
      return `${type}-${orderMarker.order.originAddress.location.latitude}-${orderMarker.order.originAddress.location.longitude}`;
    }
  };

  updateMarkers = () => {
    const orderMarkers = this.props.orders;
    const groupColors = createGroupColors(
      orderMarkers.map(orderMarker => orderMarker.group)
    );

    const markerLookup = this.markers.reduce(
      (map, multipleMarkersContainer) => {
        map.set(multipleMarkersContainer.id, multipleMarkersContainer);
        return map;
      },
      new Map<string, MapMarkerContainer>()
    );

    const generatedMarkers: {
      locationKey: string;
      type: InstructionType;
      orderMarker: OrderMarker;
    }[] = orderMarkers
      .map(orderMarker => {
        return [
          {
            locationKey: this.markerKey(orderMarker, InstructionType.Delivery),
            type: InstructionType.Delivery,
            orderMarker: orderMarker,
          },
          {
            locationKey: this.markerKey(orderMarker, InstructionType.Pickup),
            type: InstructionType.Pickup,
            orderMarker: orderMarker,
          },
        ];
      })
      .flat();

    const groupedMarkers = generatedMarkers.reduce(
      (resultingMap, currentItem) => {
        const processedMarker = {
          groupColor: groupColors.get(currentItem.orderMarker.group),
          ...currentItem.orderMarker,
        };
        if (resultingMap.has(currentItem.locationKey)) {
          resultingMap
            .get(currentItem.locationKey)
            .orderMarkers.push(processedMarker);
        } else {
          const mapMarker = new MapMarkerContainer(
            currentItem.locationKey,
            currentItem.type,
            [processedMarker]
          );

          resultingMap.set(currentItem.locationKey, mapMarker);
        }
        return resultingMap;
      },
      new Map<string, MapMarkerContainer>()
    );
    const groupedMarkersList: MapMarkerContainer[] = Array.from(
      groupedMarkers.values()
    ).flat();

    const newAndUpdatedMarkers: MapMarkerContainer[] = groupedMarkersList
      .filter(multipleMarkerContainer => {
        // All are at the same location, just do the first one
        const isHub = !(
          multipleMarkerContainer.getOrder().isSentFromHub() &&
          multipleMarkerContainer.type === InstructionType.Pickup
        );
        return isHub;
      })
      .map(multipleMarkerContainer => {
        const existingContainer = markerLookup.get(multipleMarkerContainer.id);
        if (existingContainer) {
          this.updateMarker(existingContainer, multipleMarkerContainer);
          return existingContainer;
        } else {
          multipleMarkerContainer.mapMarker = this.createMarker(
            multipleMarkerContainer
          );
          return multipleMarkerContainer;
        }
      });

    // TODO Feels heavy and stupid to do this after, should probably be moved?
    newAndUpdatedMarkers.forEach(multipleMarkerContainer => {
      if (
        this.state.locationTypes.has(multipleMarkerContainer.type) &&
        (!this.state.hideFiltered ||
          (this.state.hideFiltered && multipleMarkerContainer.isHighlighted()))
      ) {
        multipleMarkerContainer.mapMarker.setVisible(true);
      } else {
        multipleMarkerContainer.mapMarker.setVisible(false);
      }
      multipleMarkerContainer.mapMarker.setMap(this.mapInstance);
    });

    this.markers = newAndUpdatedMarkers;
  };

  infoWindowContentMultiple(
    markerContainer: MapMarkerContainer,
    selectOrderFunction: (selectedOrders: OrderListOrderClazz[]) => void
  ): ReactElement {
    return (
      <div className="planner-map-infowindow">
        {markerContainer.orderMarkers
          .sort(
            (prev, curr) => parseInt(prev.order.id) - parseInt(curr.order.id)
          )
          .map(currentMarker => {
            const currentOrder = currentMarker.order;
            return (
              <label
                style={
                  !currentMarker.rightClickable && !currentMarker.clickable
                    ? {
                        backgroundColor: 'rgba(0,0,0,0.05)',
                        cursor: 'not-allowed',
                      }
                    : null
                }
                key={`order-${currentOrder.id}-select`}
                htmlFor={`order-${currentOrder.id}-select`}
              >
                <span
                  style={{
                    backgroundColor:
                      !currentMarker.rightClickable && !currentMarker.clickable
                        ? 'rgba(0,0,0,0.05)'
                        : currentMarker.color,
                  }}
                >
                  B
                </span>
                <span>
                  <input
                    name={`order-${currentOrder.id}-select`}
                    id={`order-${currentOrder.id}-select`}
                    type="checkbox"
                    checked={currentMarker.selected}
                    disabled={
                      !currentMarker.rightClickable && !currentMarker.clickable
                    }
                    style={
                      !currentMarker.rightClickable && !currentMarker.clickable
                        ? { cursor: 'not-allowed' }
                        : null
                    }
                    onChange={() => {
                      selectOrderFunction([currentOrder]);
                    }}
                  />
                </span>
                <a
                  href={`/orders/${currentOrder.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {currentOrder.id}
                </a>{' '}
                &nbsp; &rarr; &nbsp;
                <span
                  style={{
                    backgroundColor:
                      !currentMarker.rightClickable && !currentMarker.clickable
                        ? 'rgba(0,0,0,0.05)'
                        : currentMarker.groupColor,
                  }}
                >
                  {currentOrder.windowTime()}
                </span>
              </label>
            );
          })}
      </div>
    );
  }

  createMarkerOptsForContainer(
    mapContainer: MapMarkerContainer
  ): google.maps.MarkerOptions {
    return this.createMarkerOpts(
      mapContainer.getLabel(),
      mapContainer.getOrder(),
      mapContainer.type,
      mapContainer.getGroupColor(),
      mapContainer.getColor(),
      mapContainer.getOpacity(),
      mapContainer.getBundleId()
    );
  }

  createMarkerOpts = (
    text: string,
    order: OrderListOrderClazz,
    type: InstructionType,
    groupColor: string,
    color: string,
    opacity: number,
    bundleId: string
  ) => {
    const latitude =
      type === InstructionType.Pickup
        ? order.originAddress.location.latitude
        : order.destinationAddress.location.latitude;
    const longitude =
      type === InstructionType.Pickup
        ? order.originAddress.location.longitude
        : order.destinationAddress.location.longitude;

    return {
      position: {
        lat: latitude,
        lng: longitude,
      },
      icon: mapIconColor(text, groupColor, color, order.durationInHours()),
      title: `${order.id}-${type}${bundleId ? `\n\n${bundleId} - BUNDLE` : ''}`,
      opacity: opacity,
    };
  };

  rightClickEvent = (markerContainer, updateSelectedOrders, marker) => {
    if (
      markerContainer.isRightClickAble() ||
      (!markerContainer.isRightClickAble() && markerContainer.isMultiple())
    ) {
      const targetElement = document.createElement('div');
      render(
        this.infoWindowContentMultiple(markerContainer, updateSelectedOrders),
        targetElement
      );
      this.infoWindow.setContent(targetElement);
      this.infoWindow.open(this.mapInstance, marker);
      this.setState({ expandedMarker: markerContainer });
    }
  };

  createMarker = (markerContainer: MapMarkerContainer): google.maps.Marker => {
    const { updateSelectedOrders } = this.props;
    // console.log(`RENDER`);
    const order = markerContainer.getOrder();
    const marker = new google.maps.Marker(
      this.createMarkerOptsForContainer(markerContainer)
    );
    if (markerContainer.isRightClickAble() || markerContainer.isMultiple()) {
      marker.addListener('rightclick', () =>
        this.rightClickEvent(markerContainer, updateSelectedOrders, marker)
      );
    }
    if (markerContainer.getClickable()) {
      marker.addListener('click', function () {
        updateSelectedOrders([order]);
      });
    }
    return marker;
  };

  generateHubMarkers = (): google.maps.Marker[] => {
    return this.props.hubPositions.map(hub => {
      return new google.maps.Marker({
        position: {
          lat: hub.latitude,
          lng: hub.longitude,
        },
        icon: {
          url: bigMapMarker('HUB', COLOR.WHITE, 'black'),
          anchor: new google.maps.Point(20, 40),
          scaledSize: new google.maps.Size(40, 40),
          labelOrigin: new google.maps.Point(20, 20),
        },
        label: '',
        map: this.mapInstance,
      });
    });
  };

  isMarkerEnabled = markerName => {
    const { locationTypes } = this.state;
    return locationTypes.has(markerName);
  };

  toggleMarkerTypesCheckbox = markerName => {
    const { locationTypes } = this.state;
    if (locationTypes.has(markerName)) {
      locationTypes.delete(markerName);
    } else {
      locationTypes.add(markerName);
    }

    this.setState({ locationTypes: locationTypes });
  };

  render() {
    const groupColors = createGroupColors(
      this.props.orders.map(orderMarker => orderMarker.group)
    );
    const uniqueGroups = Array.from(
      new Set(this.props.orders.map(orderMarker => orderMarker.group))
    )
      .sort()
      .map(group => (
        <div key={group} className="planner-map-legend-window">
          <img src={square(groupColors.get(group))} alt={group} /> {group}
        </div>
      ));

    return (
      <>
        <div
          id="instruction-type-filter"
          className="planner-map-instruction-filter"
        >
          <div>
            <label htmlFor="PICKUP">
              <input
                name="PICKUP"
                id="PICKUP"
                type="checkbox"
                checked={this.isMarkerEnabled(InstructionType.Pickup)}
                onChange={event =>
                  this.toggleMarkerTypesCheckbox(event.target.name)
                }
              />
              Pickups
            </label>
            <label htmlFor="DELIVERY">
              <input
                name="DELIVERY"
                id="DELIVERY"
                type="checkbox"
                checked={this.isMarkerEnabled(InstructionType.Delivery)}
                onChange={event =>
                  this.toggleMarkerTypesCheckbox(event.target.name)
                }
              />
              Deliveries
            </label>
          </div>
          <div>
            <label htmlFor="hidefiltered">
              <input
                name="hidefiltered"
                id="hidefiltered"
                type="checkbox"
                checked={this.state.hideFiltered}
                onChange={event =>
                  this.setState({
                    hideFiltered: !this.state.hideFiltered,
                  })
                }
              />
              Hide filtered
            </label>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              title={'Zoom to fit'}
              onClick={() => {
                this.zoomToOrders();
              }}
              type={'slim'}
            />
          </div>
        </div>
        <div id="planner-map-legend" className="planner-map-legend">
          <div>Click to select.</div>
          <div>Right click to show info.</div>
          <div>{uniqueGroups}</div>
        </div>
        <div
          ref={node => {
            this.mapContainer = node;
          }}
          style={{ width: '80%' }}
        />
      </>
    );
  }
}
